import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Card,
  Row,
  Col,
  Container,
  Form,
  Button,
  Modal,
  Table
} from 'react-bootstrap';
import ReactToPrint from "react-to-print";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import { convertCurrency, numberFormat, convertOrderStatus } from '../../helper';
import { GET_EXCHANGE_RATE } from '../../apollo/product/query';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GET_BANKS } from "../../apollo/bank/query";
import { COLOR_WHITE, imageRender, PRODUCTION_STATUS_PERMISSIONS, PRODUCTION_STATUS_FLOW, USER_KEY, checkProductionStatusPermission } from '../../const';
import { useSelector } from 'react-redux';
import { FaLuggageCart } from 'react-icons/fa';
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import noProductImg from "../../images/no_product.png";
import { UPDATE_PRODUCTION_STATUS } from '../../apollo/ordergroup/query';
import { successSwal, errorSwal } from '../../helper/sweetalert';

const productionStatusKeys = [
  { value: "PRODUCTION_ORDER", labelKey: "ps_production_order" },
  { value: "PATTERN", labelKey: "ps_pattern" },
  { value: "PRINTING", labelKey: "ps_printing" },
  { value: "HEAT_TRANSFER", labelKey: "ps_heat_transfer" },
  { value: "CUTTING", labelKey: "ps_cutting" },
  { value: "SEWING", labelKey: "ps_sewing" },
  { value: "QC", labelKey: "ps_qc" },
  { value: "READY_TO_COMPLETE", labelKey: "ps_ready_to_complete" },
  { value: "COMPLETED", labelKey: "ps_completed" }
];

const convertOrderType = (type, t) => {
  switch (type) {
    case 'KEY_IN':
      return "ອໍເດີສ້າງເອງ";
    case 'POS':
      return t("order_type_pos");
    case 'ONLINE':
      return t("order_type_online");
    case 'LIVE':
      return t("order_type_live");
    case 'SALE_PAGE':
      return "ຈາກ Sale Page";
    default:
      return type || '-';
  }
};

const convertLogicticsName = (name) => {
  return name || "-";
};

const convertPriceWithCommission = (totalPrice, sumPriceBaht, sumPriceUsd, commission, rate) => {
  const kip = Number(totalPrice ?? 0) || 0;
  const baht = Number(sumPriceBaht ?? 0) || 0;
  const usd = Number(sumPriceUsd ?? 0) || 0;
  const extra = Number(commission ?? 0) || 0;

  const bahtRate = Number(rate?.baht) > 0 ? Number(rate?.baht) : 1;
  const usdRate = Number(rate?.usd) > 0 ? Number(rate?.usd) : 1;

  return kip + baht * bahtRate + usd * usdRate + extra;
};
const mapStatusColor = (status) => {
  switch (status) {
    case "PRODUCTION_ORDER":
      return "secondary";
    case "PATTERN":
      return "info";
    case "PRINTING":
      return "primary";
    case "HEAT_TRANSFER":
      return "warning";
    case "CUTTING":
      return "danger";
    case "SEWING":
      return "primary";
    case "QC":
      return "info";
    case "READY_TO_COMPLETE":
      return "warning";
    case "COMPLETED":
      return "success";
    default:
      return "light";
  }
};
const mapStatusText = (status) => {
  switch (status) {
    case "HEAT_TRANSFER":
      return "dark";
    default:
      return "white";
  }
};

const getDepositStatusLabel = (status, t) => {
  switch (status) {
    case "DEPOSIT_PENDING":
      return t("deposit_pending_status");
    case "PAID_FULL":
      return t("paid_full_status");
    default:
      return "-";
  }
};

const pickFirstNonEmptyString = (value) => {
  if (!Array.isArray(value)) return "";
  const first = value.find((v) => typeof v === "string" && v.trim());
  return first || "";
};

const getOrderImageSrc = (order, emptyImage) => {
  const stockImage = pickFirstNonEmptyString(order?.stock?.image);
  if (stockImage) return imageRender(stockImage);

  const variantImage = pickFirstNonEmptyString(order?.productVariant?.image);
  if (variantImage) return imageRender(variantImage);

  return emptyImage;
};

const SalepageOrderBillA4 = React.forwardRef(function SalepageOrderBillA4({ orderGroup, variant, shopData, bankAccount }, ref) {
  const orders = Array.isArray(orderGroup?.orders) ? orderGroup.orders : [];
  const rows = orders.slice(0, 12);
  const emptyRowsCount = Math.max(0, 12 - rows.length);
  const showInternalColumns = variant === "shop";

  const getUnitSalePrice = (order) => {
    const unit = Number(order?.price || 0);
    const options = Array.isArray(order?.options)
      ? order.options.reduce((sum, opt) => sum + (Number(opt?.price) || 0), 0)
      : 0;
    return unit + options;
  };

  const getUnitCostPrice = (order) => {
    const direct = Number(order?.originPrice || 0);
    if (direct > 0) return direct;
    const variantOrigin = Number(order?.productVariant?.originPrice || 0);
    if (variantOrigin > 0) return variantOrigin;
    return 0;
  };

  const getUsedQty = (order) => Number(order?.amount || 0) || 0;
  const getFreeQty = (order) => Number(order?.amountFree || 0) || 0;
  const getTotalQty = (order) => getUsedQty(order) + getFreeQty(order);

  const getLineSaleTotal = (order) => {
    const explicit = Number(order?.totalPrice || 0);
    if (explicit > 0) return explicit;
    return getUnitSalePrice(order) * getUsedQty(order);
  };

  const getLineCostTotal = (order) => getUnitCostPrice(order) * getTotalQty(order);

  const totalUsedQty = orders.reduce((sum, o) => sum + getUsedQty(o), 0);
  const totalFreeQty = orders.reduce((sum, o) => sum + getFreeQty(o), 0);
  const totalAllQty = totalUsedQty + totalFreeQty;
  const totalSale = orders.reduce((sum, o) => sum + getLineSaleTotal(o), 0);
  const totalCost = orders.reduce((sum, o) => sum + getLineCostTotal(o), 0);
  const depositAmountValue = Number(orderGroup?.depositAmount ?? 0) || 0;
  const outstandingAmountValue = Number(orderGroup?.outstandingAmount ?? orderGroup?.debt ?? 0) || 0;

  const cell = {
    border: "1px solid #111827",
    padding: "4px 6px",
    fontSize: 11,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  };
  const th = { ...cell, fontWeight: "bold", textAlign: "center", color: "#111827" };
  const yellow = showInternalColumns ? "#ffeb3b" : "transparent";

  const shopName = shopData?.name ? `ຮ້ານ ${shopData.name}` : "";
  const shopPhone = shopData?.phone ? `ໂທ: ${shopData.phone}` : "";
  const shopEmail = shopData?.email ? `Email: ${shopData.email}` : "";
  const logoSrc = shopData?.image ? imageRender(shopData.image) : "";

  const customerName = orderGroup?.cfName ?? orderGroup?.customerName ?? "-";
  const customerPhone = orderGroup?.phone ?? "-";
  const addressText = (orderGroup?.destinationLogistic || orderGroup?.addressCf || [orderGroup?.village, orderGroup?.district, orderGroup?.province].filter(Boolean).join(", ")) || "-";
  const code = orderGroup?.code || "-";
  const createdAt = orderGroup?.createdAt ? moment(orderGroup.createdAt).format("DD/MM/YYYY HH:mm") : "-";

  return (
    <div ref={ref} style={{ width: "210mm", minHeight: "297mm", margin: "0 auto", padding: "12mm", fontFamily: "Arial, sans-serif", color: "#111827" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ width: "35%", fontSize: 12, lineHeight: 1.35 }}>
          <div>{shopPhone}</div>
          <div>{shopEmail}</div>
        </div>
        <div style={{ width: "30%", textAlign: "center" }}>
          <div style={{ fontSize: 16, fontWeight: "bold" }}>ໃບບິນເກັບເງິນ</div>
          <div style={{ fontSize: 13, fontWeight: "bold" }}>{shopName}</div>
        </div>
        <div style={{ width: "35%", display: "flex", justifyContent: "flex-end" }}>
          {logoSrc ? <img src={logoSrc} alt="logo" style={{ width: 70, height: 70, objectFit: "contain" }} /> : null}
        </div>
      </div>

      <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
        <div style={{ width: "60%" }}>
          <div>ລູກຄ້າ: {customerName}</div>
          <div>ເບີໂທ: {customerPhone}</div>
          <div>ທີ່ຢູ່: {addressText}</div>
        </div>
        <div style={{ width: "38%" }}>
          <div>ເລກທີ: {code}</div>
          <div>ວັນທີ: {createdAt}</div>
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 40 }}>ລ/ດ</th>
              <th style={{ ...th, minWidth: 220 }}>ລາຍການສິນຄ້າ</th>
              <th style={{ ...th, width: 78 }}>ຈຳນວນໄຊ້</th>
              <th style={{ ...th, width: 78 }}>ຈຳນວນແຖມ</th>
              <th style={{ ...th, width: 110 }}>ລາຄາໄຊ້</th>
              <th style={{ ...th, width: 120 }}>ລາຄາລວມ</th>
              {showInternalColumns ? <th style={{ ...th, width: 95, backgroundColor: yellow }}>ຈຳນວນລວມ</th> : null}
              {showInternalColumns ? <th style={{ ...th, width: 115, backgroundColor: yellow }}>ຕົ້ນທຶນໄຊ້</th> : null}
              {showInternalColumns ? <th style={{ ...th, width: 135, backgroundColor: yellow }}>ລາຄາຕົ້ນທຶນລວມ</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((o, idx) => {
              const usedQty = getUsedQty(o);
              const freeQty = getFreeQty(o);
              const totalQty = usedQty + freeQty;
              const unitSale = getUnitSalePrice(o);
              const unitCost = getUnitCostPrice(o);
              const saleTotal = getLineSaleTotal(o);
              const costTotal = getLineCostTotal(o);
              return (
                <tr key={o?.id || idx}>
                  <td style={{ ...cell, textAlign: "center" }}>{idx + 1}</td>
                  <td style={{ ...cell, whiteSpace: "normal" }}>{o?.productName || "-"}</td>
                  <td style={{ ...cell, textAlign: "center" }}>{usedQty || "-"}</td>
                  <td style={{ ...cell, textAlign: "center" }}>{freeQty || "-"}</td>
                  <td style={{ ...cell, textAlign: "right" }}>{unitSale ? numberFormat(unitSale) : "-"}</td>
                  <td style={{ ...cell, textAlign: "right" }}>{saleTotal ? numberFormat(saleTotal) : "-"}</td>
                  {showInternalColumns ? <td style={{ ...cell, textAlign: "center", backgroundColor: yellow }}>{totalQty || "-"}</td> : null}
                  {showInternalColumns ? <td style={{ ...cell, textAlign: "right", backgroundColor: yellow }}>{unitCost ? numberFormat(unitCost) : "-"}</td> : null}
                  {showInternalColumns ? <td style={{ ...cell, textAlign: "right", backgroundColor: yellow }}>{costTotal ? numberFormat(costTotal) : "-"}</td> : null}
                </tr>
              );
            })}
            {Array.from({ length: emptyRowsCount }).map((_, idx) => (
              <tr key={`empty-${idx}`}>
                <td style={{ ...cell, textAlign: "center" }}>{rows.length + idx + 1}</td>
                <td style={{ ...cell }}>&nbsp;</td>
                <td style={{ ...cell }}>&nbsp;</td>
                <td style={{ ...cell }}>&nbsp;</td>
                <td style={{ ...cell }}>&nbsp;</td>
                <td style={{ ...cell }}>&nbsp;</td>
                {showInternalColumns ? <td style={{ ...cell, backgroundColor: yellow }}>&nbsp;</td> : null}
                {showInternalColumns ? <td style={{ ...cell, backgroundColor: yellow }}>&nbsp;</td> : null}
                {showInternalColumns ? <td style={{ ...cell, backgroundColor: yellow }}>&nbsp;</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
        <div style={{ width: "60%" }}>
          <div>ລວມຈຳນວນໄຊ້: {numberFormat(totalUsedQty)}</div>
          <div>ລວມຈຳນວນແຖມ: {numberFormat(totalFreeQty)}</div>
          {showInternalColumns ? <div>ລວມຈຳນວນທັງໝົດ: {numberFormat(totalAllQty)}</div> : null}
        </div>
        <div style={{ width: "38%" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>ລວມຍອດ:</span>
            <span>{numberFormat(totalSale)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
            <span>ຊຳລະແລ້ວ (ມັດຈຳ):</span>
            <span>{numberFormat(depositAmountValue)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
            <span>ຄ້າງຊຳລະ:</span>
            <span>{numberFormat(outstandingAmountValue)}</span>
          </div>
          {showInternalColumns ? (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>ລວມຕົ້ນທຶນ:</span>
              <span>{numberFormat(totalCost)}</span>
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div style={{ flex: 1, fontSize: 12, lineHeight: 1.35 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>ບັນຊີທະນາຄານ</div>
          <div style={{ marginBottom: 2 }}>ທະນາຄານ: {bankAccount?.bankName || "-"}</div>
          <div style={{ marginBottom: 2 }}>ເລກບັນຊີ: {bankAccount?.bankAccount || "-"}</div>
          <div style={{ marginBottom: 2 }}>ຊື່ບັນຊີ: {bankAccount?.bankUser || "-"}</div>
        </div>
        <div style={{ width: 140, flexShrink: 0 }}>
          <div style={{ border: "1px solid #d1d5db", width: 140, height: 160, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff" }}>
            {bankAccount?.image ? (
              <img
                src={imageRender(bankAccount.image)}
                alt="qr"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : (
              <div style={{ fontSize: 11, color: "#6b7280", textAlign: "center", padding: 8 }}>
                QR
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default function SalepageOrderDetailNew({ selectedOrderGroup }) {
  const { t } = useTranslation();
  const history = useHistory();
  const productionStatuses = productionStatusKeys.map((s) => ({ value: s.value, label: t(s.labelKey) }));
  const [shopRate, setShopRate] = useState();
  const { rolePermissions } = useSelector((state) => state.permissions);
  const userData = JSON.parse(localStorage.getItem(USER_KEY));
  const isAdmin = userData?.data?.role === "ADMIN";
  const canEditReceiveDate = isAdmin || rolePermissions.includes("SALE_ACCESS") || rolePermissions.includes("REPORT_SALE_ONLY") || rolePermissions.includes("CAN_UPDATE_PRODUCTION_ORDER");
  const [showItemDetailModal, setShowItemDetailModal] = useState(false);
  const [selectedOrderItem, setSelectedOrderItem] = useState(null);
  const [isNarrowScreen, setIsNarrowScreen] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 430 : false));
  const [isSmallScreen, setIsSmallScreen] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 820 : false));
  const billA4CustomerRef = useRef(null);
  const billA4ShopRef = useRef(null);
  const billPrintCustomerRef = useRef(null);
  const billPrintShopRef = useRef(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const shopData = useMemo(() => {
    try {
      if (typeof window === "undefined") return {};
      const fromShopData = JSON.parse(localStorage.getItem("SHOP_DATA") || "null");
      const fromUser = JSON.parse(localStorage.getItem(USER_KEY) || "null")?.data?.shop;
      return fromShopData || fromUser || {};
    } catch (_) {
      return {};
    }
  }, []);
  const selectedBankAccount = useMemo(() => {
    const list = Array.isArray(bankAccounts) ? bankAccounts : [];
    return list.find((b) => b?.isActiveDefault) || list[0] || null;
  }, [bankAccounts]);
  const updatableStatuses = productionStatuses.filter(status => 
      checkProductionStatusPermission(status.value, rolePermissions)
    );
    const allowedStatuses = updatableStatuses.map(s => s.value);
    const visibleStatuses = Array.from(new Set([
      ...allowedStatuses,
      ...allowedStatuses.map(status => PRODUCTION_STATUS_FLOW[status]).filter(Boolean)
    ]));
    const [selectedStatus, setSelectedStatus] = useState(selectedOrderGroup?.productionStatus || "PRODUCTION_ORDER");
  const [receiveDate, setReceiveDate] = useState(() => ((selectedOrderGroup?.receiveDate ?? selectedOrderGroup?.recieveDate ?? selectedOrderGroup?.checkBillDate)
    ? moment.utc(selectedOrderGroup?.receiveDate ?? selectedOrderGroup?.recieveDate ?? selectedOrderGroup?.checkBillDate).local().format("DD/MM/YYYY")
    : ""));
  const [getExchangeRate, { data: loadExchangeRate }] = useLazyQuery(GET_EXCHANGE_RATE, { fetchPolicy: "cache-and-network" });
  const [updateProductionStatus, { loading: updatingStatus }] = useMutation(UPDATE_PRODUCTION_STATUS);
  const [getBanks, { data: banksData }] = useLazyQuery(GET_BANKS, { fetchPolicy: "cache-and-network" });

  useEffect(() => {
    if (loadExchangeRate) {
      setShopRate(loadExchangeRate?.exchangeRate);
    }
  }, [loadExchangeRate]);

  useEffect(() => {
    if (selectedOrderGroup?.shop?.id) {
      getExchangeRate({
        variables: {
          where: {
            shop: selectedOrderGroup?.shop?.id,
          },
        },
      });
    }
  }, [selectedOrderGroup, getExchangeRate]);

  useEffect(() => {
    if (!selectedOrderGroup?.shop?.id) return;
    getBanks({
      variables: {
        where: { shop: selectedOrderGroup.shop.id },
        orderBy: "createdAt_DESC",
        limit: 50,
      }
    });
  }, [selectedOrderGroup, getBanks]);

  useEffect(() => {
    if (banksData?.banks?.data) {
      setBankAccounts(banksData.banks.data);
    }
  }, [banksData]);


  useEffect(() => {
    if (selectedOrderGroup?.productionStatus) {
      setSelectedStatus(selectedOrderGroup.productionStatus);
    }
  }, [selectedOrderGroup]);

  useEffect(() => {
    const nextDate = selectedOrderGroup?.receiveDate ?? selectedOrderGroup?.recieveDate ?? selectedOrderGroup?.checkBillDate;
    setReceiveDate(
      nextDate
        ? moment.utc(nextDate).local().format("DD/MM/YYYY")
        : ""
    );
  }, [selectedOrderGroup]);

  useEffect(() => {
    const update = () => {
      setIsNarrowScreen(window.innerWidth <= 430);
      setIsSmallScreen(window.innerWidth <= 820);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const handleUpdateStatus = async () => {
    try {
      let utcDateTime = null;
      if (canEditReceiveDate && receiveDate) {
        const parsed = moment(receiveDate, "DD/MM/YYYY", true);
        if (!parsed.isValid()) throw new Error("Invalid receive date");
        utcDateTime = parsed.startOf("day").toISOString();
      }

      let nextStatus = selectedStatus;
      if (!isAdmin) {
        // Automatically move to the next status in the flow
        nextStatus = PRODUCTION_STATUS_FLOW[selectedOrderGroup?.productionStatus] || selectedOrderGroup?.productionStatus;
      }

      await updateProductionStatus({
        variables: {
          where: { id: selectedOrderGroup?.id },
          data: {
            status: nextStatus,
            ...(utcDateTime ? { receiveDate: utcDateTime } : {})
          },
        }
      });
      successSwal(t("update_success"));
    } catch (error) {
      errorSwal(error.message);
    }
  };

  const elementImages = useMemo(() => {
    const orders = Array.isArray(selectedOrderGroup?.orders) ? selectedOrderGroup.orders : [];
    return orders
      .flatMap((order) => (Array.isArray(order?.imagesElement) ? order.imagesElement : []))
      .map((el) => ({
        image: typeof el?.image === "string" ? el.image.trim() : "",
        detail: typeof el?.detail === "string" ? el.detail : null,
      }))
      .filter((el) => el.image);
  }, [selectedOrderGroup]);

  const handleDownloadElementImage = async (imageName) => {
    try {
      if (!imageName) return;
      const url = imageRender(imageName);
      const res = await fetch(url);
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = imageName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      errorSwal(error?.message || "Download failed");
    }
  };

  const handleDownloadAllElementImages = async () => {
    try {
      if (elementImages.length === 0) return;
      const sanitizeFileName = (name) => {
        const safe = String(name || "")
          .replace(/[<>:"/\\|?*\x00-\x1F]/g, "_")
          .replace(/\s+/g, " ")
          .trim();
        return safe.length > 60 ? safe.slice(0, 60) : safe;
      };

      const folderName = sanitizeFileName(selectedOrderGroup?.code || "imagesElement") || "imagesElement";
      const files = await Promise.all(
        elementImages.map(async (el, idx) => {
          const url = imageRender(el.image);
          const res = await fetch(url);
          if (!res.ok) throw new Error("Download failed");
          const buf = await res.arrayBuffer();
          const ext = (el.image.split(".").pop() || "png").trim();
          const base = sanitizeFileName(el.detail || el.image || `image_${idx + 1}`) || `image_${idx + 1}`;
          const filename = `${String(idx + 1).padStart(2, "0")}_${base}.${ext}`;
          return { filename, data: new Uint8Array(buf) };
        })
      );

      const crcTable = (() => {
        const table = new Uint32Array(256);
        for (let i = 0; i < 256; i++) {
          let c = i;
          for (let k = 0; k < 8; k++) {
            c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
          }
          table[i] = c >>> 0;
        }
        return table;
      })();

      const crc32 = (bytes) => {
        let c = 0xffffffff;
        for (let i = 0; i < bytes.length; i++) {
          c = crcTable[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
        }
        return (c ^ 0xffffffff) >>> 0;
      };

      const encoder = new TextEncoder();
      const chunks = [];
      const central = [];
      let offset = 0;

      const u16 = (n) => new Uint8Array([n & 0xff, (n >>> 8) & 0xff]);
      const u32 = (n) =>
        new Uint8Array([n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff]);

      for (const file of files) {
        const nameBytes = encoder.encode(file.filename);
        const dataBytes = file.data;
        const crc = crc32(dataBytes);
        const size = dataBytes.length;

        const localHeader = new Uint8Array([
          0x50, 0x4b, 0x03, 0x04,
          0x14, 0x00,
          0x00, 0x00,
          0x00, 0x00,
          0x00, 0x00,
          0x00, 0x00,
          ...u32(crc),
          ...u32(size),
          ...u32(size),
          ...u16(nameBytes.length),
          ...u16(0),
        ]);

        chunks.push(localHeader, nameBytes, dataBytes);

        const centralHeader = new Uint8Array([
          0x50, 0x4b, 0x01, 0x02,
          0x14, 0x00,
          0x14, 0x00,
          0x00, 0x00,
          0x00, 0x00,
          0x00, 0x00,
          0x00, 0x00,
          ...u32(crc),
          ...u32(size),
          ...u32(size),
          ...u16(nameBytes.length),
          ...u16(0),
          ...u16(0),
          ...u16(0),
          ...u16(0),
          ...u32(0),
          ...u32(offset),
        ]);
        central.push(centralHeader, nameBytes);

        offset += localHeader.length + nameBytes.length + dataBytes.length;
      }

      const centralStart = offset;
      for (const c of central) {
        chunks.push(c);
        offset += c.length;
      }
      const centralSize = offset - centralStart;

      const end = new Uint8Array([
        0x50, 0x4b, 0x05, 0x06,
        0x00, 0x00,
        0x00, 0x00,
        ...u16(files.length),
        ...u16(files.length),
        ...u32(centralSize),
        ...u32(centralStart),
        0x00, 0x00,
      ]);
      chunks.push(end);

      const zipBlob = new Blob(chunks, { type: "application/zip" });
      saveAs(zipBlob, `${folderName}.zip`);
    } catch (error) {
      errorSwal(error?.message || "Download failed");
    }
  };

  if (!selectedOrderGroup) return null;
  const createdAtText = moment(selectedOrderGroup?.createdAt).isValid()
    ? moment(selectedOrderGroup?.createdAt).format("DD/MM/YYYY HH:mm")
    : (selectedOrderGroup?.createdAt ?? "-");

  const depositStatusValue = selectedOrderGroup?.depositStatus;
  const depositAmountValue = Number(selectedOrderGroup?.depositAmount ?? 0) || 0;
  const outstandingAmountValue = Number(selectedOrderGroup?.outstandingAmount ?? selectedOrderGroup?.debt ?? 0) || 0;
  const isPaidFull = depositStatusValue === "PAID_FULL" || selectedOrderGroup?.isPaymented === true || outstandingAmountValue <= 0;
  const paymentStatusLabel = isPaidFull ? t("paid") : t("not_paid");
  const depositStatusLabel = getDepositStatusLabel(depositStatusValue || (isPaidFull ? "PAID_FULL" : "DEPOSIT_PENDING"), t);

  const productionLabel = productionStatuses.find((status) => status.value === selectedOrderGroup?.productionStatus)?.label;
  const openOrderItemDetail = (item) => {
    if (isSmallScreen) {
      history.push({
        pathname: "/salepage_order/item_detail",
        state: {
          item,
          orderGroupCode: selectedOrderGroup?.code || null,
        },
      });
      return;
    }
    setSelectedOrderItem(item || null);
    setShowItemDetailModal(true);
  };

  const printBill = (variant) => {
    if (variant === "shop") {
      billPrintShopRef.current?.handlePrint?.();
      return;
    }
    billPrintCustomerRef.current?.handlePrint?.();
  };

  return (
    <Container className="py-3">
      <style>{`
        .spod-detail-backdrop { z-index: 2990 !important; }
        .spod-detail-modal { z-index: 3000 !important; }
        @media (max-width: 430px) {
          .spod-detail-modal .modal-dialog {
            margin-top: 72px;
            margin-bottom: 12px;
          }
          .spod-detail-modal .modal-content {
            max-height: calc(100vh - 84px);
          }
        }
      `}</style>
      <Card className="mb-3" style={{ border: "none", background: COLOR_WHITE }}>
        <Card.Body>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => printBill("customer")}
              style={{ whiteSpace: "nowrap" }}
            >
              ບິນລູກຄ້າ
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => printBill("shop")}
              style={{ whiteSpace: "nowrap" }}
            >
              ບິນຮ້ານ
            </Button>
          </div>
          <Row>
            <Col md={6}>
              <h6>{t("customer_label")}: {selectedOrderGroup?.cfName ?? selectedOrderGroup?.customerName ?? "-"}</h6>
              <p>{t("phone_label")}: {selectedOrderGroup?.phone ?? "-"}</p>
              <p>{t("date_label")} {createdAtText}</p>
            </Col>
            <Col md={6}>
              <p>
                {t("payment_label")}: {" "}
                <Badge bg={isPaidFull ? "success" : "danger"}>
                  {paymentStatusLabel}
                </Badge>
              </p>
              <p style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                <span>{t("deposit_status_label")}:</span>
                <Badge bg={depositStatusValue === "PAID_FULL" || isPaidFull ? "success" : "warning"} text={depositStatusValue === "PAID_FULL" || isPaidFull ? "white" : "dark"}>
                  {depositStatusLabel}
                </Badge>
                <span style={{ color: "#6b7280" }}>
                  {t("deposit_amount_label")}: {numberFormat(depositAmountValue)} | {t("outstanding_amount_label")}: {numberFormat(outstandingAmountValue)}
                </span>
              </p>
              <p>{t("pay_shipping_label")}: {selectedOrderGroup?.orderDelivery?.shipmentPayType === "COD" ? t("payment_destination") : t("payment_origin")}</p>
              <p>
                {t("order_status_label")}:{" "}
                <Badge bg={mapStatusColor(selectedOrderGroup?.productionStatus)} text={mapStatusText(selectedOrderGroup?.productionStatus)}>
                  {productionLabel || convertOrderStatus(selectedOrderGroup?.status)}
                </Badge>
              </p>
              <p>{t("type_label")}: {convertOrderType(selectedOrderGroup?.type, t)}</p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {(isAdmin || visibleStatuses.includes(selectedOrderGroup?.productionStatus)) && (
        <Card className="mb-3" style={{ border: "none", background: COLOR_WHITE }}>
          <Card.Body>
            <Row className="align-items-end">
              <Col md={6}>
                <Form.Label>{t("ເລືອກຕຳແໜ່ງ")}</Form.Label>
                {isAdmin ? (
                  <Form.Select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    {productionStatuses.map((status) => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </Form.Select>
                ) : (
                  <div className="p-2 border rounded bg-light" style={{ fontWeight: "bold", height: 38 }}>
                    {checkProductionStatusPermission(selectedOrderGroup?.productionStatus, rolePermissions) 
                      ? (productionStatuses.find(s => s.value === PRODUCTION_STATUS_FLOW[selectedOrderGroup?.productionStatus])?.label || "-")
                      : (productionStatuses.find(s => s.value === selectedOrderGroup?.productionStatus)?.label || "-")
                    }
                  </div>
                )}
              </Col>
              <Col md={4}>
                <Form.Label>ວັນທີຮັບເຄື່ອງ</Form.Label>
                <Form.Control
                  type="date"
                  value={
                    receiveDate
                      ? moment(receiveDate, "DD/MM/YYYY", true).format("YYYY-MM-DD")
                      : ""
                  }
                  onChange={(e) => {
                    const iso = e?.target?.value;
                    setReceiveDate(iso ? moment(iso).format("DD/MM/YYYY") : "");
                  }}
                  style={{ width: "100%" }}
                  disabled={!canEditReceiveDate}
                />
              </Col>
              <Col md={2}>
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={handleUpdateStatus}
                  disabled={updatingStatus || selectedOrderGroup?.productionStatus === "COMPLETED" || (!isAdmin && !checkProductionStatusPermission(selectedOrderGroup?.productionStatus, rolePermissions) && !canEditReceiveDate)}
                >
                  {updatingStatus ? "..." : t("update_status")}
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <Card className="mb-3" style={{ border: "none", background: COLOR_WHITE }}>
        <Card.Body>
          {elementImages.length > 0 && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Card.Text style={{ fontWeight: "bold", margin: 0 }}>ຮູບອົງປະກອບ</Card.Text>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={handleDownloadAllElementImages}
                >
                  ດາວໂຫຼດທັງໝົດ
                </Button>
              </div>
              <Row className="g-2 mb-3">
                {elementImages.map((el, idx) => (
                  <Col key={`${el.image}-${idx}`} xs={4} sm={3} md={3} lg={2}>
                    <div
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "clamp(6px, 1.6vw, 8px)",
                        background: "#fff",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={imageRender(el.image)}
                        alt={el.detail || el.image}
                        className="img-fluid rounded"
                        style={{ width: "clamp(56px, 18vw, 80px)", height: "clamp(56px, 18vw, 80px)", objectFit: "cover" }}
                        onError={(e) => {
                          e.currentTarget.src = noProductImg;
                        }}
                      />
                      {el.detail && (
                        <div style={{ fontSize: "clamp(11px, 3.1vw, 12px)", color: "#6b7280", marginTop: 6, textAlign: "center" }}>
                          {el.detail}
                        </div>
                      )}
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="w-100"
                        style={{ marginTop: 8, fontSize: "clamp(11px, 3.1vw, 12px)" }}
                        onClick={() => handleDownloadElementImage(el.image)}
                      >
                        ດາວໂຫຼດຮູບ
                      </Button>
                    </div>
                  </Col>
                ))}
              </Row>
            </>
          )}
          <Card.Text>{t("order_items_title")}</Card.Text>
          {isSmallScreen ? (
            <div style={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <Table bordered hover size="sm" className="mb-0" style={{ background: "#fff", minWidth: 980 }}>
                <thead>
                  <tr>
                    <th style={{ width: 72 }}>ຮູບ</th>
                    <th>{t("order_items_title")}</th>
                    <th style={{ width: 160 }} className="text-end">ລາຄາ</th>
                    <th style={{ width: 110 }} className="text-end">{t("quantity_label")}</th>
                    <th style={{ width: 170 }} className="text-end">ລາຄາລວມ</th>
                    <th style={{ minWidth: 320 }} className="text-end">{t("detail")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedOrderGroup?.orders || []).map((item) => (
                    <tr key={item?.id || `${item?.productName}-${item?.cfMessage}`}>
                      <td>
                        <img
                          src={getOrderImageSrc(item, noProductImg)}
                          alt={item?.cfMessage}
                          className="img-fluid rounded"
                          style={{ width: "clamp(44px, 14vw, 60px)", height: "clamp(44px, 14vw, 60px)", objectFit: "cover" }}
                        />
                      </td>
                      <td>
                        <div style={{ fontWeight: 700, fontSize: "clamp(13px, 3.6vw, 14px)" }}>{item?.productName}</div>
                        <div style={{ fontSize: "clamp(11px, 3.2vw, 12px)", color: "#6b7280" }}>
                          {t("code_label")}: {item?.cfMessage || item?.stock?.cfMessage || "-"}
                        </div>
                      </td>
                      <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                        {"price"}: {convertCurrency(item?.currency)} {numberFormat(item?.price ?? 0)}
                      </td>
                      <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                        {Number(item?.amount ?? 0) || 0}
                        {Number(item?.amountFree ?? 0) > 0 ? ` (+${Number(item?.amountFree ?? 0)})` : ""}
                      </td>
                      <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                        {convertCurrency(item?.currency)} {numberFormat(item?.totalPrice ?? 0)}
                      </td>
                      <td className="text-end" style={{ fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>
                        {typeof item?.orderDetail === "string"
                          ? (item.orderDetail || "-")
                          : (item?.orderDetail ? JSON.stringify(item.orderDetail) : "-")}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={6} style={{ padding: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", fontSize: 12, fontWeight: 700 }}>
                        <div>{t("deposit_amount_label")}: {numberFormat(depositAmountValue)}</div>
                        <div>{t("outstanding_amount_label")}: {numberFormat(outstandingAmountValue)}</div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          ) : (
            <div style={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <Table bordered hover size="sm" className="mb-0" style={{ background: "#fff", minWidth: 980 }}>
                <thead>
                  <tr>
                    <th style={{ width: 72 }}>ຮູບ</th>
                    <th>{t("order_items_title")}</th>
                    <th style={{ width: 160 }} className="text-end">ລາຄາ</th>
                    <th style={{ width: 110 }} className="text-end">{t("quantity_label")}</th>
                    <th style={{ width: 170 }} className="text-end">ລາຄາລວມ</th>
                    <th style={{ width: 320 }} className="text-end">{t("detail")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedOrderGroup?.orders || []).map((item) => (
                    <tr
                      key={item?.id || `${item?.productName}-${item?.cfMessage}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => openOrderItemDetail(item)}
                    >
                      <td>
                        <img
                          src={getOrderImageSrc(item, noProductImg)}
                          alt={item?.cfMessage}
                          className="img-fluid rounded"
                          style={{ width: 60, height: 60, objectFit: "cover" }}
                        />
                      </td>
                      <td>
                        <div style={{ fontWeight: 700 }}>{item?.productName}</div>
                      </td>
                      <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                        {convertCurrency(item?.currency)} {numberFormat(item?.price ?? 0)}
                      </td>
                      <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                        {Number(item?.amount ?? 0) || 0}
                        {Number(item?.amountFree ?? 0) > 0 ? ` ແຖມ ${Number(item?.amountFree ?? 0)}` : ""}
                      </td>
                      <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                        {convertCurrency(item?.currency)} {numberFormat(item?.totalPrice ?? 0)}
                      </td>
                      <td className="text-end" style={{ fontSize: 12, color: "#374151", whiteSpace: "normal" }}>
                        {typeof item?.orderDetail === "string"
                          ? (item.orderDetail || "-")
                          : (item?.orderDetail ? JSON.stringify(item.orderDetail) : "-")}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={6} style={{ padding: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", fontSize: 12, fontWeight: 700 }}>
                        <div>{t("deposit_amount_label")}: {numberFormat(depositAmountValue)}</div>
                        <div>{t("outstanding_amount_label")}: {numberFormat(outstandingAmountValue)}</div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {!isSmallScreen && (
        <Modal
          className="spod-detail-modal"
          backdropClassName="spod-detail-backdrop"
          show={showItemDetailModal}
          onHide={() => setShowItemDetailModal(false)}
          centered
          size="md"
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("detail")}</Modal.Title>
            {/* <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowItemDetailModal(false)}
              style={{ marginLeft: "auto" }}
            >
              ปิด
            </Button> */}
          </Modal.Header>
          <Modal.Body
            style={{
              maxHeight: isNarrowScreen ? "calc(100vh - 190px)" : "78vh",
              overflowY: "auto",
              position: "relative",
            }}
          >
            {isNarrowScreen && (
              <Button
                type="button"
                variant="light"
                onClick={() => setShowItemDetailModal(false)}
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  width: 34,
                  height: 34,
                  borderRadius: 999,
                  border: "1px solid #e5e7eb",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  zIndex: 10,
                  boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                }}
                aria-label="Close"
              >
                ×
              </Button>
            )}
            {selectedOrderItem ? (
              <SalepageOrderItemDetailContent item={selectedOrderItem} />
            ) : null}
          </Modal.Body>
        </Modal>
      )}

      <Row>
        <Col md={6}>
          <Card style={{ boxShadow: 'none', background: COLOR_WHITE }} className="mb-3">
            <Card.Body>
              <h5>
                {t("delivery")} {convertLogicticsName(
                  selectedOrderGroup?.orderDelivery?.delivery || selectedOrderGroup?.logistic || ""
                )}
              </h5>
              <p>
                <strong>{t("tracking_no_label")}:</strong> {selectedOrderGroup?.orderDelivery?.deliveryTackingNo ?? "-"}
              </p>

              {selectedOrderGroup?.orderDelivery?.id ? <p>
                <strong>{t("delivery_address_label")}:</strong> ({selectedOrderGroup?.orderDelivery?.receiver?.branchCode ?? "-"}), {selectedOrderGroup?.orderDelivery?.receiver?.province}, {selectedOrderGroup?.orderDelivery?.receiver?.district},{' '}
                {selectedOrderGroup?.orderDelivery?.receiver?.branch}
              </p> :
                <p>
                  <strong>{t("delivery_address_label")}: {selectedOrderGroup?.destinationLogistic ?? "-"} </strong>
                </p>
              }

              <p className="text-success">
                <FaLuggageCart style={{ fontSize: 28, marginRight: 6 }} />
                {t("other_shipping_label")}: {selectedOrderGroup?.addressCf ?? ""}
              </p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card style={{ boxShadow: 'none', background: COLOR_WHITE }}>
            <Card.Body>
              <h5>{t("order_summary_title")}</h5>
              <Row className="mb-2 text-muted">
                <Col>{t("total_kip_label")}:</Col>
                <Col className="text-end">{numberFormat(selectedOrderGroup?.totalPrice ?? 0)}</Col>
              </Row>
              <Row className="mb-2 text-muted">
                <Col>{t("total_baht_label")}:</Col>
                <Col className="text-end">{numberFormat(selectedOrderGroup?.sumPriceBaht ?? 0)}</Col>
              </Row>
              <Row className="mb-2 text-muted">
                <Col>{t("total_usd_label")}:</Col>
                <Col className="text-end">{numberFormat(selectedOrderGroup?.sumPriceUsd ?? 0)}</Col>
              </Row>
              <Row className="mb-2 text-muted">
                <Col>{t("tax_label")}:</Col>
                <Col className="text-end">0</Col>
              </Row>
              <Row className="mb-2 text-muted">
                <Col>{t("deposit_amount_label")}:</Col>
                <Col className="text-end">{numberFormat(depositAmountValue)}</Col>
              </Row>
              <Row className="mb-2 text-muted">
                <Col>{t("outstanding_amount_label")}:</Col>
                <Col className="text-end">{numberFormat(outstandingAmountValue)}</Col>
              </Row>
              <Row className="mb-2 text-muted">
                <Col>{t("deposit_status_label")}:</Col>
                <Col className="text-end">
                  <Badge bg={depositStatusValue === "PAID_FULL" || isPaidFull ? "success" : "warning"} text={depositStatusValue === "PAID_FULL" || isPaidFull ? "white" : "dark"}>
                    {depositStatusLabel}
                  </Badge>
                </Col>
              </Row>
              <hr />
              <Row className="fw-bold">
                <Col>{t("amount_paid_label")}:</Col>
                <Col className="text-end">{numberFormat(convertPriceWithCommission(selectedOrderGroup?.totalPrice, selectedOrderGroup?.sumPriceBaht, selectedOrderGroup?.sumPriceUsd, 0, shopRate))} {t("kip_currency")}</Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div style={{ position: "absolute", left: -99999, top: 0 }}>
        <SalepageOrderBillA4
          ref={billA4CustomerRef}
          orderGroup={selectedOrderGroup}
          variant="customer"
          shopData={shopData}
          bankAccount={selectedBankAccount}
        />
        <SalepageOrderBillA4
          ref={billA4ShopRef}
          orderGroup={selectedOrderGroup}
          variant="shop"
          shopData={shopData}
          bankAccount={selectedBankAccount}
        />
      </div>
      <ReactToPrint
        ref={billPrintCustomerRef}
        content={() => billA4CustomerRef.current}
        pageStyle="@page { size: A4; margin: 12mm; } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }"
      />
      <ReactToPrint
        ref={billPrintShopRef}
        content={() => billA4ShopRef.current}
        pageStyle="@page { size: A4; margin: 12mm; } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }"
      />
    </Container>
  );
}

function SalepageOrderItemDetailContent({ item }) {
  const { t } = useTranslation();
  if (!item) return null;
  return (
    <Row className="g-3">
      <Col xs={12} md={4}>
        <img
          src={getOrderImageSrc(item, noProductImg)}
          alt={item?.cfMessage}
          className="img-fluid rounded"
          style={{ width: "100%", maxHeight: 280, objectFit: "cover" }}
          onError={(e) => {
            e.currentTarget.src = noProductImg;
          }}
        />
      </Col>
      <Col xs={12} md={8}>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>
          {item?.productName || "-"}
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
          {t("code_label")}: {item?.cfMessage || item?.stock?.cfMessage || "-"}
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            {t("quantity_label")}:{" "}
            <span style={{ color: "#111827", fontWeight: 700 }}>
              {Number(item?.amount ?? 0) || 0}
              {Number(item?.amountFree ?? 0) > 0 ? ` (+${Number(item?.amountFree ?? 0)})` : ""}
            </span>
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", textAlign: "right" }}>
            ລາຄາ:{" "}
            <span style={{ color: "#111827", fontWeight: 700 }}>
              {convertCurrency(item?.currency)} {numberFormat(item?.totalPrice)}
            </span>
          </div>
        </div>

        <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
          {t("detail")}:{" "}
          <span style={{ color: "#111827", fontWeight: 600 }}>
            {typeof item?.orderDetail === "string"
              ? (item.orderDetail || "-")
              : (item?.orderDetail ? JSON.stringify(item.orderDetail) : "-")}
          </span>
        </div>

        {typeof item?.shopSize === "string" && item?.shopSize ? (
          <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
            ໄຊຕ໌:{" "}
            <span style={{ color: "#111827", fontWeight: 600 }}>
              {item.shopSize}
            </span>
          </div>
        ) : null}

        {Array.isArray(item?.imagesElement) && item.imagesElement.length > 0 ? (
                  <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#374151" }}>ຮູບອົງປະກອບ</div>
            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {item.imagesElement
                .filter((x) => typeof x?.image === "string" && x.image.trim())
                .map((x, idx) => (
                          <div key={`${x.image}-${idx}`} style={{ width: "clamp(54px, 16vw, 70px)" }}>
                    <img
                      src={imageRender(x.image)}
                      alt={x.detail || x.image}
                      className="img-fluid rounded"
                              style={{ width: "clamp(54px, 16vw, 70px)", height: "clamp(54px, 16vw, 70px)", objectFit: "cover" }}
                      onError={(e) => {
                        e.currentTarget.src = noProductImg;
                      }}
                    />
                  </div>
                ))}
            </div>
          </div>
        ) : null}
      </Col>
    </Row>
  );
}

export function SalepageOrderItemDetailPage() {
  const history = useHistory();
  const location = useLocation();
  const state = location?.state || {};
  const item = state?.item || null;
  const orderGroupCode = state?.orderGroupCode || null;
  const { t } = useTranslation();

  return (
    <Container className="py-3">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <Button variant="outline-secondary" size="sm" onClick={() => history.goBack()}>
          ກັບຄືນ
        </Button>
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          {orderGroupCode ? `${t("order_code")}: ${orderGroupCode}` : ""}
        </div>
      </div>

      <Card style={{ border: "none", background: COLOR_WHITE }}>
        <Card.Body>
          {item ? (
            <SalepageOrderItemDetailContent item={item} />
          ) : (
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              {t("detail")} -
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export function SalepageOrderGroupDetailPage() {
  const history = useHistory();
  const location = useLocation();
  const state = location?.state || {};
  const selectedOrderGroup = state?.selectedOrderGroup || null;
  const { t } = useTranslation();

  return (
    <Container className="py-3">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <Button variant="outline-secondary" size="sm" onClick={() => history.goBack()}>
          ກັບຄືນ
        </Button>
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          {selectedOrderGroup?.code ? `${t("order_code")}: ${selectedOrderGroup.code}` : ""}
        </div>
      </div>

      {selectedOrderGroup ? (
        <SalepageOrderDetailNew selectedOrderGroup={selectedOrderGroup} />
      ) : (
        <Card style={{ border: "none", background: COLOR_WHITE }}>
          <Card.Body>
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              {t("order_detail_title")}
            </div>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}
